import type { FC } from 'react'
import { useCallback, useEffect } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import { useSidebarContext } from '../context'
import { useSidebarHover, useSidebarState } from '../hooks'
import {
  ANIMATION_DURATION_MS,
  COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  SIDEBAR_Z_INDEX,
} from '../constants'
import { SidebarBackdrop } from './SidebarBackdrop'
import { SidebarHeader } from './SidebarHeader'
import { CollapsedRail } from './CollapsedRail'

interface SectionProps {
  readonly isCollapsed?: boolean
}

type SectionComponent = FC<SectionProps>

interface SidebarProps {
  readonly StatusSection: SectionComponent
  readonly NavigationSection: SectionComponent
  readonly MenuSection: SectionComponent
  readonly UserSection: SectionComponent
}

export const Sidebar: FC<SidebarProps> = ({
  StatusSection,
  NavigationSection,
  MenuSection,
  UserSection,
}) => {
  const { setHovering, dispatch } = useSidebarContext()
  const { isPinned, shouldShowExpanded, togglePin, expand } = useSidebarState()

  const { sidebarRef, handleMouseEnter } = useSidebarHover({
    enabled: !isPinned,
  })

  const closeSidebar = useCallback(() => {
    if (!isPinned) {
      setHovering(false)
      dispatch({ type: 'SET_EXPANDED', expanded: false })
    }
  }, [isPinned, setHovering, dispatch])

  const handleBackdropClick = useCallback(() => {
    closeSidebar()
  }, [closeSidebar])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSidebar()
      }
    },
    [closeSidebar],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const showBackdrop = !isPinned && shouldShowExpanded

  if (!shouldShowExpanded) {
    return (
      <Box sx={collapsedContainerSx}>
        <CollapsedRail
          onExpand={expand}
          StatusSection={StatusSection}
          NavigationSection={NavigationSection}
          MenuSection={MenuSection}
          UserSection={UserSection}
        />
      </Box>
    )
  }

  return (
    <>
      <SidebarBackdrop visible={showBackdrop} onClick={handleBackdropClick} />

      <Box ref={sidebarRef} onMouseEnter={handleMouseEnter} sx={expandedContainerSx(isPinned)}>
        <SidebarHeader isPinned={isPinned} onCollapse={closeSidebar} onTogglePin={togglePin} />

        <Box sx={statusSectionSx}>
          <StatusSection />
        </Box>

        <Box sx={navigationSectionSx}>
          <NavigationSection />
        </Box>

        <Box sx={spacerSx} />

        <Box sx={menuSectionSx}>
          <MenuSection />
        </Box>

        <Box sx={userSectionSx}>
          <UserSection />
        </Box>
      </Box>
    </>
  )
}

const collapsedContainerSx: SxProps<Theme> = {
  position: 'fixed',
  left: 0,
  top: 0,
  height: '100vh',
  zIndex: SIDEBAR_Z_INDEX,
}

const expandedContainerSx = (isPinned: boolean): SxProps<Theme> => ({
  position: 'fixed',
  left: 0,
  top: 0,
  width: SIDEBAR_WIDTH,
  height: '100vh',
  backgroundColor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
  zIndex: SIDEBAR_Z_INDEX,
  display: 'flex',
  flexDirection: 'column',
  animation: isPinned ? 'none' : `slideIn ${ANIMATION_DURATION_MS}ms ease-out`,
  '@keyframes slideIn': {
    from: {
      transform: `translateX(-${SIDEBAR_WIDTH - COLLAPSED_WIDTH}px)`,
      opacity: 0.8,
    },
    to: {
      transform: 'translateX(0)',
      opacity: 1,
    },
  },
})

const statusSectionSx: SxProps<Theme> = {
  px: 2,
  py: 2,
  borderBottom: 1,
  borderColor: 'divider',
}

const navigationSectionSx: SxProps<Theme> = {
  px: 1,
  py: 1,
}

const spacerSx: SxProps<Theme> = {
  flex: 1,
}

const menuSectionSx: SxProps<Theme> = {
  px: 1,
  py: 1,
  borderTop: 1,
  borderColor: 'divider',
}

const userSectionSx: SxProps<Theme> = {
  marginTop: 'auto',
  px: 2,
  py: 2,
  borderTop: 1,
  borderColor: 'divider',
}
